import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Callout, Icon, Panel, ProgressBar, Text } from '../../../../components';
import { LumioMark } from '../../../creatives/components/LumioMark';
import type { SiteRegenState } from '../../art-style-api';
import {
  failedPartLabels,
  isLookAction,
  isRunningPhase,
  siteActivityCopy,
  siteFailureCopy,
  siteFailureTitle,
  type SiteAction,
} from '../../site-regen';
import {
  Root,
  Head,
  Lead,
  Mark,
  Copy,
  Eyebrow,
  Title,
  Status,
  Note,
  Actions,
  PreviewLink,
  Progress,
  Reason,
  Parts,
  PartsLabel,
  PartList,
  Part,
} from './SiteRegenCard.styles';

export type SiteRegenCardProps = {
  /** The brand's website look and its latest site job (never idle here). */
  state: SiteRegenState;
  /** The name of the style the art was drawn in (for a catalog look's job, the look's), as owners read it ("" when not
   *  known). */
  styleName: string;
  /** The brand's site showing that art before the switch ("" when its address is not known). */
  previewUrl: string;
  /** Whether a regeneration in flight replaces the art players see (undefined: not known). */
  inPlace: boolean | undefined;
  /** A site action's request is on its way (the actions wait for it). */
  requesting: SiteAction | null;
  onSwitch: () => void;
  /** "Set as primary" for the catalog look whose art was just drawn (it asks first). */
  onApplyLook?: () => void;
  /** "Back to the original look" on a live site: given only when the strip cannot offer the original look as a card
   *  (the brand's looks are unavailable or empty), so the owner can always put the original back. */
  onRevert?: () => void;
  /** Runs the failed job's action again. */
  onRetry: () => void;
};

/** Failed parts listed by name before the rest are counted. */
const MAX_PARTS = 6;

/**
 * The brand's website look on the Art Style page, from its latest site job: drawing (the site switches once all of
 * it is drawn), ready (a regeneration from before that: preview it, then switch), switching, live, reverting, original,
 * or failed (what failed, casino-creation's reason, Retry). A catalog look's job names the look: its art drawn (ready:
 * preview it, then set it as primary) or the site switched to it. Going back to another look is that look's card's
 * "Set as primary". It survives a reload: the page reads it from spark-creatives and follows the job. There is no
 * Stop: these jobs always run to the end.
 */
export const SiteRegenCard = ({
  state,
  styleName,
  previewUrl,
  inPlace,
  requesting,
  onSwitch,
  onApplyLook,
  onRevert,
  onRetry,
}: SiteRegenCardProps): React.ReactElement => {
  const { t } = useTranslation();
  const { phase } = state;
  const running = isRunningPhase(phase);
  const locked = requesting !== null;
  const style = { style: styleName };
  const look = isLookAction(state.action);

  let title: React.ReactNode;
  if (look && phase === 'switching' && styleName) {
    title = <Title text="Switching your site to {{style}}" placeholders={style} tag="h2" />;
  } else if (look && phase === 'ready' && styleName) {
    title = <Title text="Your website art in {{style}} is ready" placeholders={style} tag="h2" />;
  } else if (phase === 'drawing') {
    title = styleName ? (
      <Title text="Drawing your website art in {{style}}" placeholders={style} tag="h2" />
    ) : (
      <Title text="Drawing your website art" tag="h2" />
    );
  } else if (phase === 'switching') {
    title = <Title text="Switching your site to your new art" tag="h2" />;
  } else if (phase === 'reverting') {
    title = <Title text="Putting your original look back" tag="h2" />;
  } else if (phase === 'ready') {
    title = <Title text="Your new website art is ready" tag="h2" />;
  } else if (phase === 'live') {
    title = styleName ? (
      <Title text="Your website shows {{style}}" placeholders={style} tag="h2" />
    ) : (
      <Title text="Your website shows your art style" tag="h2" />
    );
  } else if (phase === 'original') {
    title = <Title text="Your website shows its original look" tag="h2" />;
  } else {
    title = <Title text={siteFailureTitle(state.action, t)} tag="h2" />;
  }

  let status: React.ReactNode = null;
  if (running) {
    status = <Status text={siteActivityCopy(state, t)} tag="paragraph" />;
  } else if (phase === 'ready' && look) {
    status = (
      <Status
        text="Your site keeps its current look until you set this one as primary. Preview it on your site first."
        tag="paragraph"
      />
    );
  } else if (phase === 'ready') {
    status = (
      <Status
        text="Your site keeps its current look until you switch. Preview it on your site first."
        tag="paragraph"
      />
    );
  } else if (phase === 'live') {
    status = <Status text="Players see this art now." tag="paragraph" />;
  } else if (phase === 'original') {
    status = state.preview_param ? (
      <Status text="Your new art is kept. You can switch to it again at any time." tag="paragraph" />
    ) : null;
  } else if (phase === 'failed') {
    status = <Status text={siteFailureCopy(state, t)} tag="paragraph" />;
  }

  let note: React.ReactNode = null;
  if (phase === 'drawing' && look) {
    note = (
      <Note
        text="Players keep seeing your current look while this is drawn. It can take a while; you can leave this page."
        tag="paragraph"
      />
    );
  } else if (phase === 'drawing') {
    note =
      inPlace === true ? (
        <Note
          text="Your site already shows this style, so players see the new art as each part is drawn. It can take a few hours; you can leave this page."
          tag="paragraph"
        />
      ) : inPlace === false ? (
        <Note
          text="Your site switches to the new art once all of it is drawn. It can take a few hours; you can leave this page."
          tag="paragraph"
        />
      ) : (
        <Note text="It can take a few hours; you can leave this page." tag="paragraph" />
      );
  } else if (phase === 'switching' || phase === 'reverting') {
    note = <Note text="Nothing is redrawn. Players see the change once this is done." tag="paragraph" />;
  }

  const preview = previewUrl ? (
    <PreviewLink
      as="a"
      href={previewUrl}
      target="_blank"
      rel="noopener noreferrer"
      $variant="outline"
      $fullWidth={false}
      $iconOnly={false}
    >
      <Icon name="external-link" size={18} />
      <Text text="Preview on your site" tag="span" />
    </PreviewLink>
  ) : null;

  let actions: React.ReactNode = null;
  if (phase === 'ready' && look) {
    actions = (
      <>
        {preview}
        {onApplyLook ? (
          <Button label={t('Set as primary')} icon="globe" disabled={locked} onClick={() => !locked && onApplyLook()} />
        ) : null}
      </>
    );
  } else if (phase === 'ready') {
    actions = (
      <>
        {preview}
        <Button label={t('Switch my site')} icon="globe" disabled={locked} onClick={() => !locked && onSwitch()} />
      </>
    );
  } else if (phase === 'live' && state.original_style_id && onRevert) {
    actions = (
      <Button
        label={t('Back to the original look')}
        variant="outline"
        icon="refresh"
        disabled={locked}
        onClick={() => !locked && onRevert()}
      />
    );
  } else if (phase === 'original' && state.preview_param) {
    actions = (
      <>
        {preview}
        <Button
          label={t('Switch my site')}
          variant="outline"
          icon="globe"
          disabled={locked}
          onClick={() => !locked && onSwitch()}
        />
      </>
    );
  } else if (phase === 'failed') {
    actions = (
      <Button
        label={t('Retry')}
        variant="outline"
        icon="refresh"
        disabled={locked}
        onClick={() => !locked && onRetry()}
      />
    );
  }

  const parts = phase === 'failed' ? failedPartLabels(state.failed_parts, t) : [];
  const shownParts = parts.slice(0, MAX_PARTS);
  const outdated = !look && state.spark_style_outdated && (phase === 'ready' || phase === 'live');

  return (
    <Panel>
      <Root aria-label={t('Your website art')}>
        <Head>
          <Lead>
            <Mark $tone={phase === 'failed' ? 'danger' : 'neutral'} aria-hidden="true">
              {phase === 'failed' ? (
                <Icon name="warning" size={22} />
              ) : phase === 'drawing' || phase === 'ready' ? (
                <LumioMark size={22} />
              ) : (
                <Icon name="globe" size={22} />
              )}
            </Mark>
            <Copy role="status" aria-live="polite">
              <Eyebrow text="Your website art" tag="span" />
              {title}
              {status}
              {note}
            </Copy>
          </Lead>
          {actions ? <Actions>{actions}</Actions> : null}
        </Head>
        {running && state.steps_total > 0 ? (
          <Progress>
            <ProgressBar value={Math.min(100, (state.steps_done / state.steps_total) * 100)} animate />
          </Progress>
        ) : null}
        {phase === 'failed' && state.error_message ? (
          <Reason text="Reason given: {{reason}}" placeholders={{ reason: state.error_message }} tag="paragraph" />
        ) : null}
        {parts.length > 0 ? (
          <Parts>
            <PartsLabel text="Not finished" tag="span" />
            <PartList>
              {shownParts.map((label) => (
                <Part key={label}>
                  <Text text={label} tag="span" />
                </Part>
              ))}
              {parts.length > MAX_PARTS ? (
                <Part>
                  <Text text="+{{n}} more" placeholders={{ n: parts.length - MAX_PARTS }} tag="span" />
                </Part>
              ) : null}
            </PartList>
          </Parts>
        ) : null}
        {outdated ? (
          <Callout
            tone="info"
            text="Your active style changed since this art was drawn. Regenerate your website art to bring it up to date."
          />
        ) : null}
      </Root>
    </Panel>
  );
};

export default SiteRegenCard;
